type RequestContext = {
    endpoint?: string;
    method?: string;
};

type SubmitErrorLogDetails = {
    method?: string;
    endpoint?: string;
    status?: number | string;
    code?: string;
    message: string;
    errorMessage: string;
};

const NO_RESPONSE_STATUS = "no_response";

export const normalizeSubmitErrorForLog = (
    error: any,
    requestContext: RequestContext = {}
): SubmitErrorLogDetails => {
    const config = error?.config || error?.response?.config || {};
    const status = error?.response?.status ?? error?.status;
    const method = normalizeMethod(requestContext.method || config.method);
    const endpoint = normalizeEndpoint(requestContext.endpoint || config.url);
    const code = normalizeString(error?.code);
    const message =
        normalizeString(error?.message) ||
        normalizeResponseMessage(error?.response?.data?.message) ||
        normalizeResponseMessage(error?.data?.message) ||
        normalizeString(error?.name) ||
        "Unknown submit error";

    const details: SubmitErrorLogDetails = {
        method,
        endpoint,
        status: status || NO_RESPONSE_STATUS,
        code,
        message,
        errorMessage: ""
    };

    details.errorMessage = buildSubmitErrorMessage(details);
    return details;
};

export const getSubmitFunctionRequestContext = (
    submitFunction: any
): RequestContext => {
    const requestContext = submitFunction?._compleoApiRequest || {};

    return {
        endpoint: normalizeString(requestContext.endpoint),
        method: normalizeString(requestContext.verb || requestContext.method)
    };
};

const buildSubmitErrorMessage = (details: SubmitErrorLogDetails) => {
    const parts = [
        formatPart("method", details.method),
        formatPart("endpoint", details.endpoint),
        formatPart("status", details.status),
        formatPart("code", details.code),
        formatPart("message", details.message)
    ].filter(Boolean);

    return `Error submitting form: ${parts.join(" ")}`;
};

const normalizeMethod = (method?: string) =>
    normalizeString(method)?.toUpperCase();

const normalizeEndpoint = (endpoint?: string) => {
    const normalizedEndpoint = normalizeString(endpoint);

    if (!normalizedEndpoint) {
        return undefined;
    }

    try {
        const origin =
            typeof window === "undefined"
                ? "https://app.compleo.local"
                : window.location.origin;
        const url = new URL(normalizedEndpoint, origin);
        return url.pathname;
    } catch {
        return normalizedEndpoint.split("?")[0].split("#")[0];
    }
};

const normalizeResponseMessage = (message: any) => {
    if (typeof message !== "string") {
        return undefined;
    }

    return normalizeString(message);
};

const normalizeString = (value: any) => {
    if (typeof value !== "string") {
        return undefined;
    }

    const trimmedValue = value.trim();

    return trimmedValue || undefined;
};

const formatPart = (key: string, value?: number | string) => {
    if (value === undefined || value === null || value === "") {
        return "";
    }

    return `${key}=${value}`;
};
